import { getSchedulesForClubs, SCHEDULE_FIELDS } from '@/api/schedule/scheduleApi';
import type { ClubSchedulesSlice, ClubScheduleSelection } from '../engine/types';
import type { Tournament, Availability, Club } from '@/types/tournament.model';
import type { Schedule as ApiSchedule } from '@/types/schedule.model';
import { knockoutStartOf } from '@/utils/tournament-schedule';

function normalizeTime(t: string): string {
  const s = t.trim();
  const parts = s.split(':').map((p) => p.trim());
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return s.length >= 5 ? s.slice(0, 5) : s;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Stable JSON fingerprint for comparing expanded day blocks (tournament vs catalog). */
export function fingerprintAvailabilities(days: Availability[]): string {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const normalized = sorted.map((day) => ({
    date: day.date,
    dayOfWeek: day.dayOfWeek,
    timeRange: [...day.timeRange]
      .sort((a, b) =>
        normalizeTime(a.startTime).localeCompare(normalizeTime(b.startTime)),
      )
      .map((tr) => ({
        qty: tr.qty,
        courtsQuantity: tr.courtsQuantity,
        startTime: normalizeTime(tr.startTime),
        endTime: normalizeTime(tr.endTime),
      })),
  }));
  return JSON.stringify(normalized);
}

/**
 * Two blocks can share every row and differ only in their knockout cut (moving the cut means
 * creating another block), so the cut is part of the block's fingerprint.
 */
function fingerprintBlock(
  days: Availability[],
  cut: { knockoutStartDate?: string | null; knockoutStartTime?: string | null },
): string {
  const start = knockoutStartOf(cut);
  return `${fingerprintAvailabilities(days)}|${start ? `${start.date}T${start.startTime}` : ''}`;
}

function fingerprintCatalogSchedule(s: ApiSchedule): string {
  return fingerprintBlock((s.schedule || []) as Availability[], s);
}

/** Wizard PATCH payload uses a flat list of `{ id }` without nested club/day blocks. */
function isFlatScheduleIdsPayload(
  schedule: unknown,
): schedule is Array<{ id: string }> {
  if (!Array.isArray(schedule) || schedule.length === 0) return false;
  const first = schedule[0];
  if (first === null || typeof first !== 'object') return false;
  return (
    'id' in first &&
    typeof (first as { id: unknown }).id === 'string' &&
    !('club' in first)
  );
}

function isExpandedScheduleChunks(
  schedule: Tournament['schedule'],
): schedule is Array<{ club: Club; schedule: Availability[] }> {
  if (!Array.isArray(schedule) || schedule.length === 0) return false;
  const first = schedule[0] as Record<string, unknown>;
  return (
    first !== null &&
    typeof first === 'object' &&
    'club' in first &&
    'schedule' in first &&
    Array.isArray(first.schedule)
  );
}

/**
 * Builds {@link ClubSchedulesSlice} for the edit modal by resolving either:
 * - flat `{ id }[]` schedule references (wizard save format), or
 * - expanded nested day blocks (GET response) matched to catalog schedules by content.
 */
export async function hydrateClubSchedulesSliceFromTournament(
  tournament: Tournament,
): Promise<ClubSchedulesSlice> {
  const matchDuration = Number(tournament.matchDuration);
  const empty: ClubSchedulesSlice = {
    clubSchedules: [],
    validForMatchDuration: null,
  };

  if (!tournament.schedule?.length) {
    return empty;
  }

  if (isFlatScheduleIdsPayload(tournament.schedule)) {
    return hydrateFromFlatScheduleIds(tournament, tournament.schedule, matchDuration);
  }

  if (isExpandedScheduleChunks(tournament.schedule)) {
    return hydrateFromExpandedChunks(tournament, matchDuration);
  }

  return empty;
}

async function hydrateFromFlatScheduleIds(
  tournament: Tournament,
  flatSchedule: Array<{ id: string }>,
  matchDuration: number,
): Promise<ClubSchedulesSlice> {
  const idSet = new Set(flatSchedule.map((s) => s.id));
  const clubIds = tournament.clubs?.map((c) => c.id) ?? [];
  if (clubIds.length === 0 || idSet.size === 0) {
    return {
      clubSchedules: [],
      validForMatchDuration: null,
    };
  }

  const results = await getSchedulesForClubs(clubIds, SCHEDULE_FIELDS.DEFAULT);
  const byClub = new Map<string, Set<string>>();

  for (const { clubId, schedules } of results) {
    for (const s of schedules) {
      if (idSet.has(s.id)) {
        if (!byClub.has(clubId)) byClub.set(clubId, new Set());
        byClub.get(clubId)!.add(s.id);
      }
    }
  }

  const clubSchedules = buildClubScheduleSelections(tournament, byClub);
  const hasIds = clubSchedules.some((cs) => cs.scheduleIds.length > 0);

  return {
    clubSchedules,
    validForMatchDuration: hasIds ? matchDuration : null,
  };
}

async function hydrateFromExpandedChunks(
  tournament: Tournament,
  matchDuration: number,
): Promise<ClubSchedulesSlice> {
  const chunks = tournament.schedule as Array<{
    club: Club;
    schedule: Availability[];
    id?: string;
    knockoutStartDate?: string | null;
    knockoutStartTime?: string | null;
  }>;

  const clubIds = Array.from(new Set(chunks.map((c) => c.club.id)));
  if (clubIds.length === 0) {
    return { clubSchedules: [], validForMatchDuration: null };
  }

  const results = await getSchedulesForClubs(clubIds, SCHEDULE_FIELDS.DEFAULT);
  const catalogByClub = new Map(results.map((r) => [r.clubId, r.schedules]));

  const selectionByClub = new Map<string, Set<string>>();

  for (const chunk of chunks) {
    const clubId = chunk.club.id;
    const days = chunk.schedule || [];
    if (days.length === 0) continue;

    const catalogs = catalogByClub.get(clubId) ?? [];
    // The block id is the reliable match; the content fingerprint covers responses without it.
    const fp = fingerprintBlock(days, chunk);
    const matched =
      catalogs.find((c) => chunk.id !== undefined && c.id === chunk.id) ??
      catalogs.find((c) => fingerprintCatalogSchedule(c) === fp);

    if (!selectionByClub.has(clubId)) {
      selectionByClub.set(clubId, new Set());
    }
    if (matched) {
      selectionByClub.get(clubId)!.add(matched.id);
    }
  }

  const clubSchedules = buildClubScheduleSelections(tournament, selectionByClub);

  const hasIds = clubSchedules.some((cs) => cs.scheduleIds.length > 0);

  return {
    clubSchedules,
    validForMatchDuration: hasIds ? matchDuration : null,
  };
}

function buildClubScheduleSelections(
  tournament: Tournament,
  idsByClub: Map<string, Set<string>>,
): ClubScheduleSelection[] {
  const out: ClubScheduleSelection[] = [];
  const seen = new Set<string>();

  const resolveClub = (clubId: string): Club | undefined =>
    tournament.clubs?.find((c) => c.id === clubId) ??
    (tournament.schedule as Array<{ club: Club }>).find(
      (s) => s.club.id === clubId,
    )?.club;

  if (tournament.clubs?.length) {
    for (const c of tournament.clubs) {
      seen.add(c.id);
      const ids = idsByClub.get(c.id);
      out.push({
        clubId: c.id,
        club: c,
        scheduleIds: ids ? Array.from(ids).sort() : [],
      });
    }
  }

  for (const clubId of idsByClub.keys()) {
    if (seen.has(clubId)) continue;
    const club = resolveClub(clubId);
    if (!club) continue;
    seen.add(clubId);
    out.push({
      clubId,
      club,
      scheduleIds: Array.from(idsByClub.get(clubId)!).sort(),
    });
  }

  return out;
}
